import SEOManager from './components/SEOManager';
import useMetaPixel from './hooks/useMetaPixel';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
import { Suspense, lazy } from 'react';
import CookieConsentBanner from './components/CookieConsentBanner';
import AutocompleteInput from './components/AutocompleteInput';
import { AuthProvider } from './pages/AuthContext';
import { RealtimeProvider } from './realtime/RealtimeContext';
import ProtectedCRM from './components/ProtectedCRM';
import { testStripeOnboard } from './pages/testStripeOnboard';
import React, { useEffect } from "react";
import { QuizProvider } from "./context/QuizContext";

// Convertiti da import statico a lazy() il 2026-09-15: queste ~49 pagine
// (blog + Dashboard/Login/Registrazione/PayDiretta/IdentityReturn) finivano
// tutte dentro main.js (2,65MB), scaricato ed eseguito su OGNI pagina anche
// se l'utente visitava solo la home. PayDiretta in particolare chiamava
// loadStripe() a livello di modulo, quindi Stripe.js si caricava in modo
// bloccante anche sugli articoli del blog. Vedi CLAUDE conversazione
// caricamento lento / Core Web Vitals mobile.
const Valori = lazy(() => import('./pages/Valori'));
const Login = lazy(() => import('./pages/Login'));
const DashboardOperatore = lazy(() => import('./pages/DashboardOperatore'));
const Assistenti = lazy(() => import('./pages/assistenti'));
const Registrazione = lazy(() => import('./pages/Registrazione'));
const RegistrazioneRapida = lazy(() => import('./pages/RegistrazioneRapida'));
const RegistrazioneIBAN = lazy(() => import('./pages/RegistrazioneIBAN'));
const PayDiretta = lazy(() => import('./pages/PayDiretta'));
const IdentityReturn = lazy(() => import('./pages/IdentityReturn'));
const BadanteAdOreMilano = lazy(() => import('./pages/blog/BadanteAdOreMilano'));
const AssistenzaAnzianiNapoli = lazy(() => import('./pages/blog/AssistenzaAnzianiNapoli'));
const AssistenzaAnzianiTorino = lazy(() => import('./pages/blog/AssistenzaAnzianiTorino'));
const QuantoCostaAssistenteAdOre = lazy(() => import('./pages/blog/QuantoCostaAssistenteAdOre'));
const BadanteConviventeOAdOre = lazy(() => import('./pages/blog/BadanteConviventeOAdOre'));
const ComeSceglierAssistente = lazy(() => import('./pages/blog/ComeSceglierAssistente'));
const AssistenzaAnzianiMonza = lazy(() => import('./pages/blog/AssistenzaAnzianiMonza'));
const AccompagnamentoVisiteMilano = lazy(() => import('./pages/blog/AccompagnamentoVisiteMilano'));
const AccompagnamentoVisiteTorino = lazy(() => import('./pages/blog/AccompagnamentoVisiteTorino'));
const AccompagnamentoVisiteNapoli = lazy(() => import('./pages/blog/AccompagnamentoVisiteNapoli'));
const AccompagnamentoVisiteMonza = lazy(() => import('./pages/blog/AccompagnamentoVisiteMonza'));
const AccompagnamentoVisiteGenova = lazy(() => import('./pages/blog/AccompagnamentoVisiteGenova'));
const AccompagnamentoVisiteRoma = lazy(() => import('./pages/blog/AccompagnamentoVisiteRoma'));
const CompagniaAnzianiDomicilio = lazy(() => import('./pages/blog/CompagniaAnzianiDomicilio'));
const AssistenzaAdOreVsAgenzia = lazy(() => import('./pages/blog/AssistenzaAdOreVsAgenzia'));
const AiutareAnzianoTecnologia = lazy(() => import('./pages/blog/AiutareAnzianoTecnologia'));
const AssistenzaDisabiliAdulti = lazy(() => import('./pages/blog/AssistenzaDisabiliAdulti'));
const SegnaliAnzianoBisognoSupporto = lazy(() => import('./pages/blog/SegnaliAnzianoBisognoSupporto'));
const ComeParlareGenitoreAnziano = lazy(() => import('./pages/blog/ComeParlareGenitoreAnziano'));
const SolitudineAnziani = lazy(() => import('./pages/blog/SolitudineAnziani'));
const AiutoSpesaCommissioni = lazy(() => import('./pages/blog/AiutoSpesaCommissioni'));
const SicurezzaFamiglie = lazy(() => import('./pages/blog/SicurezzaFamiglie'));
const SolitudineAnzianiFeste = lazy(() => import('./pages/blog/SolitudineAnzianiFeste'));
const AssistenzaLeggeraMilano = lazy(() => import('./pages/blog/AssistenzaLeggeraAnzianiMilano'));
const AssistenzaLeggeraGenova = lazy(() => import('./pages/blog/AssistenzaLeggeraAnzianiGenova'));
const AssistenzaAnzianiGenova = lazy(() => import('./pages/blog/AssistenzaAnzianiGenova'));
const AssistenzaLeggeraTorino = lazy(() => import('./pages/blog/AssistenzaLeggeraTorino'));
const AssistenzaLeggeraNapoli = lazy(() => import('./pages/blog/AssistenzaLeggeraNapoli'));
const AssistenzaLeggeraMonza = lazy(() => import('./pages/blog/AssistenzaLeggeraMonza'));
const AssistenzaAnzianiAdOreRoma = lazy(() => import('./pages/blog/AssistenzaAnzianiAdOreRoma'));
const CargiverItaliani = lazy(() => import('./pages/blog/CaregiverItaliani'));
const BonuseAgevolazioni2026 = lazy(() => import('./pages/blog/BonuseAgevolazioni2026'));
const ComeFunzionaMima = lazy(() => import('./pages/blog/ComeFunzionaMima'));
const ItalianiMangianoDaSoli = lazy(() => import('./pages/blog/ItalianiCheMangianoSoli'));
const Accompagnamentopostoperatorio = lazy(() => import('./pages/blog/Accompagnamentopostoperatorio'));
const AccompagnamentoVisiteAnzianiDisabili = lazy(() => import('./pages/blog/AccompagnamentoVisiteAnzianiDisabili'));
const BabysittingBambiniDisabilita = lazy(() => import('./pages/blog/BabysittingBambiniDisabilita'));
const TutoringDSAADHD = lazy(() => import('./pages/blog/TutoringDSAADHD'));
const SupportoABADomicilio = lazy(() => import('./pages/blog/SupportoABADomicilio'));
const EducatoreProfessionaleDomicilio = lazy(() => import('./pages/blog/EducatoreProfessionaleDomicilio'));

const FormRichiesta = lazy(() => import('./pages/FormRichiesta'));
const RichiesteOperatori = lazy(() => import('./pages/RichiesteOperatori'));
const RichiesteUtente = lazy(() => import('./pages/RichiesteUtente'));
const DashboardUtente = lazy(() => import('./pages/DashboardUtente'));
const ProfiloOperatore = lazy(() => import('./pages/ProfiloOperatore'));
const ProfiloOperatoreDettagliato = lazy(() => import('./pages/ProfiloOperatoreDettagliato'));
const ProfiloUtenteDettagliato = lazy(() => import('./pages/ProfiloUtenteDettagliato'));
const StoricoRichiesteUtente = lazy(() => import('./pages/StoricoRichiesteUtente'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const ChatList = lazy(() => import('./pages/ChatList'));
const ScriviRecensione = lazy(() => import('./pages/ScriviRecensione'));
const StoricoRichiesteOperatore = lazy(() => import('./pages/StoricoRichiesteOperatore'));
const Blog = lazy(() => import('./pages/Blog'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const CondizioniDiUso = lazy(() => import('./pages/CondizioniDiUso'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const ScriviRecensioneInterazione = lazy(() => import('./pages/ScriviRecensioneInterazione'));
const AboutUs = lazy(() => import('./pages/Aboutus'));
const HomePage = lazy(() => import('./pages/HomePage'));
const ProfiloUtente = lazy(() => import('./pages/ProfiloUtente'));
const LavoraConNoi = lazy(() => import('./pages/LavoraConNoi'));
const Faq = lazy(() => import('./pages/Faq'));
const OnboardingCompletato = lazy(() => import('./pages/OnboardingCompletato'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const FormSegnalazione = lazy(() => import('./pages/FormSegnalazione'));
const FormSegnalazioneOperatore = lazy(() => import('./pages/FormSegnalazioneOperatore'));
const ImpostazioniAccount = lazy(() => import('./pages/ImpostazioniAccount'));
const CRMPage = lazy(() => import('./pages/CRM'));
const AdminRuoliPage = lazy(() => import('./pages/AdminRuoli'));
const ElencoOperatori = lazy(() => import('./pages/ElencoOperatori'));
const PreventivoDirettoForm = lazy(() => import('./pages/PreventivoDirettoForm'));
const RichiestaDettaglioOperatore = lazy(() => import('./pages/RichiestaDettaglioOperatore'));
const CheckoutRichiesteDirette = lazy(() => import('./pages/checkoutRichiesteDirette'));
const Utenti = lazy(() => import('./pages/utenti'));
const LandingLocal = lazy(() => import('./pages/LandingLocal'));
const TrovaOperatore = lazy(() => import('./pages/TrovaOperatore'));
const AdminOnboarding = lazy(() => import('./pages/AdminOnboarding'));
const Step1 = lazy(() => import('./pages/Step1'));
const Step2 = lazy(() => import('./pages/Step2'));
const Step3 = lazy(() => import('./pages/Step3'));
const Step4 = lazy(() => import('./pages/Step4'));
const Step5 = lazy(() => import('./pages/Step5'));
const RichiestaAttivata = lazy(() => import('./pages/RichiestaAttivata'));

const CrmRichiestePage = lazy(() => import('./pages/Crmrichiestepage'));
function MetaPixelWrapper() {
    useMetaPixel();
    return null;
}
function App() {
    useEffect(() => {

        const updateVH = () => {
            const vh =
                window.visualViewport?.height || window.innerHeight;

            document.documentElement.style.setProperty(
                "--vh",
                `${vh * 0.01}px`
            );
        };

        updateVH();

        window.addEventListener("resize", updateVH);

        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", updateVH);
            window.visualViewport.addEventListener("scroll", updateVH);
        }

        return () => {
            window.removeEventListener("resize", updateVH);
            window.visualViewport?.removeEventListener("resize", updateVH);
            window.visualViewport?.removeEventListener("scroll", updateVH);
        };

    }, []);

    return (
        <AuthProvider>
            <RealtimeProvider>
                <Router>

                    <MetaPixelWrapper />
                    <ScrollToTop />
                    <SEOManager />

                    <Suspense fallback={<div style={{ padding: 24 }}>Caricamento in corso …</div>}>
                        <QuizProvider>
                            <Routes>

                                <Route path="/test-stripe-onboard" element={<button onClick={testStripeOnboard}>Test Stripe Onboard</button>} />
                                <Route path="/utenti" element={<Utenti />} />
                                <Route path="/" element={<HomePage />} />
                                <Route path="/home" element={<AboutUs />} />
                                <Route path="/login" element={<Login />} />
                                <Route path="/registrazione" element={<Registrazione />} />
                                <Route path="/registrazione-rapida" element={<RegistrazioneRapida />} />

                                <Route path="/registrazione-iban" element={<RegistrazioneIBAN />} />
                                <Route path="/onboarding-completato" element={<OnboardingCompletato />} />
                                <Route path="/condizionidiuso" element={<CondizioniDiUso />} />
                                <Route path="/privacy" element={<PrivacyPolicy />} />
                                <Route path="/cookiepolicy" element={<CookiePolicy />} />
                                <Route path="/cookieconsent" element={<CookieConsentBanner />} />
                                <Route path="/lavora-con-noi" element={<LavoraConNoi />} />
                                <Route path="/faq" element={<Faq />} />


                                <Route path="/valori" element={<Valori />} />
                                <Route path="/assistenza-anziani-italia" element={<Valori />} />
                                <Route path="/assistenti" element={<Assistenti />} />
                                <Route path="/:city" element={<LandingLocal />} />
                                <Route path="/blog" element={<Blog />} />
                                <Route path="/blog/assistenza-anziani-ad-ore-milano" element={<Navigate to="/blog/badante-ad-ore-milano" replace />} />
                                <Route path="/blog/badante-ad-ore-milano" element={<BadanteAdOreMilano />} />
                                <Route path="/blog/assistenza-anziani-ad-ore-napoli" element={<AssistenzaAnzianiNapoli />} />
                                <Route path="/blog/assistenza-anziani-ad-ore-torino" element={<AssistenzaAnzianiTorino />} />
                                <Route path="/blog/quanto-costa-assistente-ad-ore-anziani" element={<QuantoCostaAssistenteAdOre />} />
                                <Route path="/blog/badante-convivente-o-ad-ore" element={<BadanteConviventeOAdOre />} />
                                <Route path="/blog/come-scegliere-assistente-per-anziano" element={<ComeSceglierAssistente />} />
                                <Route path="/blog/assistenza-anziani-ad-ore-genova" element={<AssistenzaAnzianiGenova />} />
                                <Route path="/blog/assistenza-anziani-monza-brianza" element={<AssistenzaAnzianiMonza />} />
                                <Route path="/blog/accompagnamento-anziani-visite-mediche-milano" element={<AccompagnamentoVisiteMilano />} />
                                <Route path="/blog/accompagnamento-anziani-visite-mediche-torino" element={<AccompagnamentoVisiteTorino />} />
                                <Route path="/blog/accompagnamento-anziani-visite-mediche-napoli" element={<AccompagnamentoVisiteNapoli />} />
                                <Route path="/blog/accompagnamento-anziani-visite-mediche-monza" element={<AccompagnamentoVisiteMonza />} />
                                <Route path="/blog/accompagnamento-anziani-visite-mediche-genova" element={<AccompagnamentoVisiteGenova />} />
                                <Route path="/blog/accompagnamento-anziani-visite-mediche-roma" element={<AccompagnamentoVisiteRoma />} />
                                <Route path="/blog/compagnia-anziani-domicilio" element={<CompagniaAnzianiDomicilio />} />
                                <Route path="/blog/assistenza-ad-ore-vs-agenzia-badanti" element={<AssistenzaAdOreVsAgenzia />} />
                                <Route path="/blog/aiutare-anziano-con-tecnologia" element={<AiutareAnzianoTecnologia />} />
                                <Route path="/blog/assistenza-domiciliare-disabili-adulti" element={<AssistenzaDisabiliAdulti />} />
                                <Route path="/blog/segnali-anziano-bisogno-di-supporto" element={<SegnaliAnzianoBisognoSupporto />} />
                                <Route path="/blog/come-parlare-genitore-anziano-accettare-aiuto" element={<ComeParlareGenitoreAnziano />} />
                                <Route path="/blog/solitudine-anziani-cause-conseguenze" element={<SolitudineAnziani />} />
                                <Route path="/blog/aiuto-spesa-commissioni-anziani" element={<AiutoSpesaCommissioni />} />
                                <Route path="/blog/sicurezza-assistenti-cosa-fare-rischio" element={<Navigate to="/blog" replace />} />
                                <Route path="/blog/benessere-cosa-fare" element={<SicurezzaFamiglie />} />
                                <Route path="/blog/solitudine-anziani-dopo-le-feste-pasqua" element={<SolitudineAnzianiFeste />} />
                                <Route path="/blog/assistenza-leggera-anziani-milano" element={<AssistenzaLeggeraMilano />} />
                                <Route path="/blog/assistenza-leggera-anziani-genova" element={<AssistenzaLeggeraGenova />} />
                                <Route path="/blog/assistenza-leggera-anziani-torino" element={<AssistenzaLeggeraTorino />} />
                                <Route path="/blog/assistenza-leggera-anziani-napoli" element={<AssistenzaLeggeraNapoli />} />
                                <Route path="/blog/assistenza-leggera-anziani-monza" element={<AssistenzaLeggeraMonza />} />
                                <Route path="/blog/assistenza-anziani-ad-ore-roma" element={<AssistenzaAnzianiAdOreRoma />} />
                                <Route path="/blog/caregiver-italiani" element={<CargiverItaliani />} />
                                <Route path="/blog/bonus-agevolazioni-2026" element={<BonuseAgevolazioni2026 />} />
                                <Route path="/blog/come-funziona-mima" element={<ComeFunzionaMima />} />
                                <Route path="/blog/hantavirus-cosa-sapere-oggi" element={<Navigate to="/blog" replace />} />
                                <Route path="/blog/italiani-che-mangiano-soli" element={<ItalianiMangianoDaSoli />} />
                                <Route path="/blog/accompagnamento-post-operatorio" element={<Accompagnamentopostoperatorio />} />
                                <Route path="/blog/accompagnamento-visite-anziani-disabili" element={<AccompagnamentoVisiteAnzianiDisabili />} />
                                <Route path="/blog/babysitting-bambini-disabilita" element={<BabysittingBambiniDisabilita />} />
                                <Route path="/blog/tutoring-dsa-adhd" element={<TutoringDSAADHD />} />
                                <Route path="/blog/supporto-aba-domicilio" element={<SupportoABADomicilio />} />
                                <Route path="/blog/educatore-professionale-domicilio" element={<EducatoreProfessionaleDomicilio />} />

                                <Route path="/trova-operatore" element={<TrovaOperatore />} />
                                <Route path="/step1" element={<Step1 />} />
                                <Route path="/step2" element={<Step2 />} />
                                <Route path="/step3" element={<Step3 />} />
                                <Route path="/step4" element={<Step4 />} />
                                <Route path="/step5" element={<Step5 />} />
                                <Route path="/richiesta-attivata" element={<RichiestaAttivata />} />

                                <Route path="/form-segnalazione" element={<FormSegnalazione />} />
                                <Route path="/form-segnalazione-operatore" element={<FormSegnalazioneOperatore />} />
                                <Route path="/impostazioni-account" element={<ImpostazioniAccount />} />




                                <Route path="/operatore" element={<ProtectedRoute><DashboardOperatore /></ProtectedRoute>} />
                                <Route path="/operatori/richieste" element={<ProtectedRoute><RichiesteOperatori /></ProtectedRoute>} />
                                <Route path="/profilo-operatore" element={<ProtectedRoute><ProfiloOperatore /></ProtectedRoute>} />
                                <Route path="/profilo-operatore/:id" element={<ProtectedRoute><ProfiloOperatoreDettagliato /></ProtectedRoute>} />
                                <Route path="/storico-operatore" element={<ProtectedRoute><StoricoRichiesteOperatore /></ProtectedRoute>} />

                                <Route path="/utente/richieste" element={<ProtectedRoute><RichiesteUtente /></ProtectedRoute>} />
                                <Route path="/richiesta" element={<ProtectedRoute><FormRichiesta /></ProtectedRoute>} />
                                <Route path="/utente" element={<ProtectedRoute><DashboardUtente /></ProtectedRoute>} />
                                <Route path="/profilo-utente" element={<ProtectedRoute><ProfiloUtente /></ProtectedRoute>} />
                                <Route path="/profilo-utente/:idUtente" element={<ProtectedRoute><ProfiloUtenteDettagliato /></ProtectedRoute>} />
                                <Route path="/richiesta/:richiestaId" element={<RichiestaDettaglioOperatore />} />

                                <Route path="/operatori" element={<ElencoOperatori />} />
                                <Route path="/preventivo/:operatoreId" element={<PreventivoDirettoForm />} />
                                <Route path="/checkout-diretta/:richiestaId" element={<CheckoutRichiesteDirette />} />

                                <Route path="/checkout/:richiestaId" element={<CheckoutPage />} />
                                <Route path="/pay-diretta/:richiestaId" element={<PayDiretta />} />
                                <Route path="/identity/return" element={<IdentityReturn />} />

                                <Route path="/storico-richieste" element={<ProtectedRoute><StoricoRichiesteUtente /></ProtectedRoute>} />

                                <Route path="/chat/:idRichiesta" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
                                <Route path="/chat" element={<ProtectedRoute><ChatList /></ProtectedRoute>} />
                                <Route path="/scrivi-recensione/:id" element={<ProtectedRoute><ScriviRecensione /></ProtectedRoute>} />
                                <Route path="/recensione-interazione/:idRichiesta" element={<ProtectedRoute><ScriviRecensioneInterazione /></ProtectedRoute>} />
                                <Route path="/autocomplete" element={<AutocompleteInput />} />




                                <Route path="/admin/ruoli" element={<AdminRuoliPage />} />
                                <Route path="/crm" element={<ProtectedCRM><CRMPage /></ProtectedCRM>} />
                                <Route path="/crm/richieste" element={<ProtectedCRM><CrmRichiestePage /></ProtectedCRM>} />
                                <Route path="/admin/onboarding" element={<AdminOnboarding />} />

                            </Routes>
                        </QuizProvider>
                    </Suspense>
                    <CookieConsentBanner />
                </Router>
            </RealtimeProvider>
        </AuthProvider>
    );
}

export default App;
